import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import { useMemo } from 'react';
import { PART_NUMBER_DESCRIPTIONS, computePeakConcurrentByDay } from '../../utils/billing';
import KpiCard from './KpiCard';

// The four-tile KPI row that sits above the deployments table on both
// the team-scoped and company-wide deployment views. Totals are derived
// from the same `deployments` array the table renders so the numbers
// stay in sync with the rows beneath.
//
// `Max Peak Usage` is the maximum number of units of this
// part number that were deployed at any moment during the selected
// billing month. The peak is computed from the deployment-level
// `quantity` field using the half-open `[provision_date,
// decommission_date)` interval -- see
// `computePeakConcurrentByDay` for the full algorithm. The KPI and the
// per-day bar chart read from the same util so the KPI value equals
// the tallest bar in the chart below.
DeploymentsKpiRow.propTypes = {
  partNumber: PropTypes.string.isRequired,
  deployments: PropTypes.array,
  month: PropTypes.instanceOf(Date)
};

DeploymentsKpiRow.defaultProps = {
  deployments: [],
  month: null
};

export default function DeploymentsKpiRow({ partNumber, deployments, month }) {
  const totalQuantity = useMemo(
    () => deployments.reduce((sum, d) => sum + (d.quantity || 0), 0),
    [deployments]
  );
  // Monthly peak: max concurrent units reached during the selected
  // month. Falls back to 0 when the month hasn't been seeded yet or
  // there are no deployments so the tile still renders a clean number.
  const monthlyPeak = useMemo(() => {
    const result = computePeakConcurrentByDay(deployments, month);
    return result ? result.monthlyPeak : 0;
  }, [deployments, month]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <KpiCard label="Part Number" value={partNumber} emphasized />
      </Grid>
      <Grid item xs={12} sm={4}>
        <KpiCard label="Max Peak Usage" value={monthlyPeak} emphasized />
      </Grid>
      <Grid item xs={12} sm={4}>
        <KpiCard label="Total Quantity Deployed" value={totalQuantity} />
      </Grid>
      <Grid item xs={12}>
        <KpiCard
          label="Part Number Description"
          value={PART_NUMBER_DESCRIPTIONS[partNumber] || '-'}
          valueVariant="body2"
        />
      </Grid>
    </Grid>
  );
}
